/**
 * SuggestionTriggerService.js  (v2 — Firebase-native)
 *
 * Connect:  ws://<RAILWAY_URL>/ws/<roomId>
 * Server bootstraps round structure + transcripts from Firebase automatically.
 * Client only needs to send: force_suggest | end_session | ping
 */
class SuggestionTriggerService {
    constructor() {
      this.ws = null
      this.roomId = null
      this.isConnected = false
      this.reconnectAttempts = 0
      this.maxReconnectAttempts = 5
      this.reconnectTimer = null
      this._pingInterval = null

      // Set this before calling initialize()
      this.onSuggestionReceived = null   // (suggestion) => void
      this.onConfigured = null           // () => void
      this.onThinking = null             // () => void
      this.onError = null                // (code, message) => void
  }

    // ── Public API ──────────────────────────────────────────────────────────────

    /**
     * @param {string} roomId  — Firebase room document ID
     */
    initialize(roomId) {
        this.roomId = roomId
        const BASE_URL = process.env.REACT_APP_INTERVIEW_COPILOT_URL
        const wsUrl = `${BASE_URL.replace(/^http/, "ws")}/ws/${roomId}`
        console.log("[SuggestionTrigger] Connecting to room:", roomId)
        this._connect(wsUrl)
    }

    /** Force a suggestion immediately (e.g. interviewer clicks button) */
    forceSuggest() {
        this._send({ type: "force_suggest" })
    }

    /** Call when interview ends */
    endSession() {
        this._send({ type: "end_session" })
    }

    cleanup() {
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
        this._stopPing()
        if (this.ws) {
            this.ws.onclose = null // prevent reconnect loop on intentional close
            this.ws.close()
            this.ws = null
        }
      this.isConnected = false
      console.log("[SuggestionTrigger] Cleaned up")
  }

    // ── Private ─────────────────────────────────────────────────────────────────

    _connect(wsUrl) {
        this.ws = new WebSocket(wsUrl)

        this.ws.onopen = () => {
            console.log("[SuggestionTrigger] WebSocket connected ✓")
            this.isConnected = true
            this.reconnectAttempts = 0
            this._startPing()
        }

      this.ws.onmessage = (event) => {
          try {
              const msg = JSON.parse(event.data)
              this._handleMessage(msg)
          } catch (e) {
              console.error("[SuggestionTrigger] Failed to parse message:", e)
          }
    }

      this.ws.onerror = (err) => {
          console.error("[SuggestionTrigger] WebSocket error:", err)
      }

      this.ws.onclose = () => {
          console.warn("[SuggestionTrigger] WebSocket closed")
          this.isConnected = false
          this._stopPing()
          this._scheduleReconnect(wsUrl)
      }
  }

    _handleMessage(msg) {
        switch (msg.type) {
            case "configured":
                console.log("[SuggestionTrigger] Session configured ✓", msg)
                if (this.onConfigured) this.onConfigured()
                break

            case "thinking":
                console.log("[SuggestionTrigger] Agent is generating...")
                if (this.onThinking) this.onThinking()
                break

            case "suggestion":
                console.log("[SuggestionTrigger] Suggestion received:", msg.question?.text)
                if (this.onSuggestionReceived) {
                    this.onSuggestionReceived({
                        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                        timestamp: new Date(),
                        sender: "ai",
                        type: "suggestion",
                        // Question
                        text: msg.question?.text,
                        questionType: msg.question?.question_type,
                        targetSkill: msg.question?.target_skill,
                        rationale: msg.question?.rationale,
                        listenFor: msg.question?.listen_for,
                        coachingNote: msg.question?.coaching_note,
                        // Context
                        assessment: msg.assessment,
                        urgency: msg.urgency,
                        currentSection: msg.current_section,
                        currentSkill: msg.current_skill,
                        skillsRemaining: msg.skills_remaining,
                        elapsedMinutes: msg.elapsed_minutes,
                        moveOn: msg.move_on,
                        moveOnNote: msg.move_on_note,
                    })
        }
            break

        case "skipped":
            console.log("[SuggestionTrigger] Skipped:", msg.reason)
            break

        case "section_transition":
            console.log(`[SuggestionTrigger] Section: ${msg.from_section} → ${msg.to_section}`)
            break

        case "time_warning":
            console.log(`[SuggestionTrigger] ⏱ ${msg.minutes_remaining}min left (${msg.scope})`)
            break

        case "session_summary":
            console.log("[SuggestionTrigger] Session summary:", msg)
            break

        case "error":
            console.error("[SuggestionTrigger] Server error:", msg.code, msg.message)
            if (this.onError) this.onError(msg.code, msg.message)
            break

        case "pong":
            break

          default:
              console.log("[SuggestionTrigger] Unknown message:", msg.type)
      }
  }

    _send(payload) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn("[SuggestionTrigger] WS not open, dropping:", payload.type)
            return
        }
      this.ws.send(JSON.stringify(payload))
  }

    _scheduleReconnect(wsUrl) {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error("[SuggestionTrigger] Max reconnect attempts reached")
            return
        }
      const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000)
      this.reconnectAttempts++
      console.log(`[SuggestionTrigger] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)
      this.reconnectTimer = setTimeout(() => this._connect(wsUrl), delay)
  }

    _startPing() {
        this._pingInterval = setInterval(() => {
            this._send({ type: "ping" })
        }, 30000)
    }

    _stopPing() {
        if (this._pingInterval) {
            clearInterval(this._pingInterval)
            this._pingInterval = null
        }
    }
}

export default SuggestionTriggerService